import { DOCUMENT } from '@angular/common';
import { Injectable, inject, signal } from '@angular/core';
import { BREAKPOINTS } from '../const/Breakpoints';

export type MobileZoomLevel = 'COMPACT' | 'NORMAL' | 'LARGE';

/**
 * « Taille d'affichage sur mobile » (Préférences → Affichage mobile).
 *
 * Le zoom passe par la balise viewport (`initial-scale` = `maximum-scale`),
 * comme un vrai zoom du navigateur. Ne PAS utiliser la propriété CSS `zoom` :
 * elle réduit aussi les unités vw / vh, et tous les panneaux et dialogues
 * plein écran rapetissent (essayé puis retiré le 2026-11-02).
 *
 * - Téléphone seulement : le plus petit côté de l'écran ≤ 599 px. Ailleurs,
 *   l'échelle reste 1.
 * - `maximum-scale` = l'échelle choisie : iOS ne zoome pas tout seul quand on
 *   touche un champ.
 * - Le choix est gardé sur l'appareil (localStorage), pas sur le compte.
 * - Le script en ligne de `src/index.html` applique la même logique avant le
 *   démarrage d'Angular ; les deux doivent rester identiques.
 * - Chrome sur ordinateur ignore la balise viewport : pour simuler un iPhone
 *   à 0.9, prévisualiser une largeur de 393 / 0.9 ≈ 437 px.
 */
@Injectable({ providedIn: 'root' })
export class MobileZoomService {
  static readonly STORAGE_KEY = 'GT_MOBILE_ZOOM';
  static readonly DEFAULT_LEVEL: MobileZoomLevel = 'NORMAL';
  static readonly LEVELS: Record<MobileZoomLevel, number> = {
    COMPACT: 0.85,
    NORMAL: 0.9,
    LARGE: 1,
  };

  private readonly document = inject(DOCUMENT);

  readonly level = signal<MobileZoomLevel>(this.readStoredLevel());

  /** Appelé une fois au démarrage (AppComponent.ngOnInit). */
  apply(): void {
    const meta = this.document.querySelector('meta[name="viewport"]');
    if (meta) {
      meta.setAttribute('content', this.viewportContent());
    }
  }

  setLevel(level: MobileZoomLevel): void {
    this.level.set(level);
    try {
      localStorage.setItem(MobileZoomService.STORAGE_KEY, level);
    } catch {
      // Navigation privée : le choix vaut pour la session seulement.
    }
    this.apply();
  }

  viewportContent(): string {
    const scale = this.isPhoneScreen()
      ? MobileZoomService.LEVELS[this.level()]
      : 1;
    return `width=device-width, initial-scale=${scale}, maximum-scale=${scale}, user-scalable=no`;
  }

  private isPhoneScreen(): boolean {
    const screen = this.document.defaultView?.screen;
    return (
      !!screen && Math.min(screen.width, screen.height) <= BREAKPOINTS.PHONE_MAX
    );
  }

  private readStoredLevel(): MobileZoomLevel {
    try {
      const stored = localStorage.getItem(MobileZoomService.STORAGE_KEY);
      if (stored && stored in MobileZoomService.LEVELS) {
        return stored as MobileZoomLevel;
      }
    } catch {
      // localStorage indisponible
    }
    return MobileZoomService.DEFAULT_LEVEL;
  }
}
